/**
 * PremiumSocialProof
 *
 * Beta-stage rewrite. Previously this section was misnamed —
 * it had a "platform layers" list, vague stats (1 Platform / 1
 * Workflow / Unlimited), and tech logos labeled as social proof.
 * None of it was actual social proof, and during beta we don't
 * have customer quotes / usage numbers / logos to show.
 *
 * Honest beta-stage approach: own that we're in beta, surface the
 * Founding Member program as the credibility signal, and lean on
 * tech-stack credibility (Cloudflare, Stripe) rather than fake
 * social-proof framing.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import useFoundingSpots from '../../hooks/useFoundingSpots';
import Testimonials from './Testimonials';
import { TESTIMONIALS } from '../../data/testimonials';
import './PremiumSocialProof.css';

const PremiumSocialProof = () => {
  const spots = useFoundingSpots();
  return (
    <section className="premium-social-proof">
      <div className="social-proof-container">
        {/* Beta-stage honesty + scarcity. The Founding Member program
            is the most credible "proof" we have right now — and the
            scarcity is real (Stripe coupon enforces 25-redemption cap). */}
        <div className="activity-feed">
          <div className="activity-header">
            <span className="hp-live-indicator">
              <span className="hp-pulse-dot" />
              We're in beta
            </span>
          </div>
          <div className="hp-activity-list">
            <div className="hp-activity-item">
              <span className="hp-activity-icon">🌱</span>
              <span className="activity-text">
                {spots.isFull ? (
                  <><strong>Founding Member spots are full</strong> — thank you, founders.</>
                ) : (
                  <>
                    <strong>
                      {spots.remaining != null ? `${spots.remaining} of ${spots.cap} Founding Member spots left` : `${spots.cap} Founding Member spots`}
                    </strong> at $59/mo, then $79.{' '}
                    <Link to="/pricing" style={{ color: '#C9A227', textDecoration: 'none', fontWeight: 600 }}>
                      Claim yours →
                    </Link>
                  </>
                )}
              </span>
            </div>
            <div className="hp-activity-item">
              <span className="hp-activity-icon">🔒</span>
              <span className="activity-text">
                <strong>Founding rate</strong> — locked forever, even after public launch
              </span>
            </div>
            <div className="hp-activity-item">
              <span className="hp-activity-icon">🤝</span>
              <span className="activity-text">
                <strong>White-glove onboarding</strong> — 30-min call with the team to get your first webinar live
              </span>
            </div>
            <div className="hp-activity-item">
              <span className="hp-activity-icon">⚡</span>
              <span className="activity-text">
                <strong>Direct line to the founder</strong> — feedback shapes what ships next
              </span>
            </div>
          </div>
        </div>

        {/* Tech-stack credibility. Buyers want to know the platform
            is real — built on Cloudflare (the cloud everyone trusts),
            Stripe (real payments), and modern AI. Not dressed up as
            social proof; just signals of legitimacy. */}
        {/* Real quotes replace the "Built on" strip once there are some. */}
        <Testimonials />
        {TESTIMONIALS.length === 0 && (
        <div className="tech-logos">
          <span className="powered-label">Built on</span>
          <div className="hp-logo-grid">
            <div className="tech-logo-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              <span>Global CDN</span>
            </div>
            <div className="tech-logo-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
              </svg>
              <span>Secure Payments</span>
            </div>
            <div className="tech-logo-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2M7.5 13A2.5 2.5 0 0 0 5 15.5 2.5 2.5 0 0 0 7.5 18a2.5 2.5 0 0 0 2.5-2.5A2.5 2.5 0 0 0 7.5 13m9 0a2.5 2.5 0 0 0-2.5 2.5 2.5 2.5 0 0 0 2.5 2.5 2.5 2.5 0 0 0 2.5-2.5 2.5 2.5 0 0 0-2.5-2.5z"/>
              </svg>
              <span>AI Powered</span>
            </div>
            <div className="tech-logo-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
              <span>SSL Secured</span>
            </div>
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

export default PremiumSocialProof;
